import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../api.ts";
import { zsynchronizujAkcent } from "../motyw.ts";
import { useStore } from "../store.ts";
import { Ekran, Komunikat, Pole, Przycisk } from "../ui.tsx";

/**
 * Ekran linku zaproszeniowego — zakładanie konta albo ustawianie nowego hasła.
 *
 * Jeden mechanizm obsługuje oba przypadki, bo różnią się tylko tym, czy nick
 * już istnieje. Serwer mówi, o który chodzi, więc klient nie musi zgadywać
 * z kształtu adresu.
 */
export function Invite({ token, onDone }: { token: string; onDone: () => void }) {
  const setUser = useStore((state) => state.setUser);
  const [opis, setOpis] = useState<{ purpose: "register" | "password_reset"; targetNick: string | null } | null>(
    null,
  );
  const [nick, setNick] = useState("");
  const [haslo, setHaslo] = useState("");
  const [haslo2, setHaslo2] = useState("");
  const [blad, setBlad] = useState<string | null>(null);
  const [trwa, setTrwa] = useState(false);

  useEffect(() => {
    api
      .describeInvite(token)
      .then(setOpis)
      .catch((error: unknown) =>
        setBlad(error instanceof ApiError ? error.message : "Link jest nieważny."),
      );
  }, [token]);

  const wyslij = async (event: FormEvent) => {
    event.preventDefault();
    if (haslo !== haslo2) {
      setBlad("Hasła się różnią.");
      return;
    }
    setBlad(null);
    setTrwa(true);
    try {
      const { user } =
        opis?.purpose === "password_reset"
          ? await api.resetPassword(token, haslo)
          : await api.register(token, nick, haslo);
      setUser(user);
      zsynchronizujAkcent(user.kolor);
      onDone();
    } catch (error) {
      setBlad(error instanceof ApiError ? error.message : "Nie udało się.");
    } finally {
      setTrwa(false);
    }
  };

  if (blad && !opis) {
    return (
      <Ekran tytul="Zaproszenie">
        <Komunikat rodzaj="blad">{blad}</Komunikat>
        <Przycisk pelna onClick={onDone}>
          Wróć do logowania
        </Przycisk>
      </Ekran>
    );
  }

  if (!opis) {
    return (
      <Ekran tytul="Zaproszenie">
        <Komunikat>Sprawdzam link…</Komunikat>
      </Ekran>
    );
  }

  const reset = opis.purpose === "password_reset";

  return (
    <Ekran
      tytul={reset ? "Nowe hasło" : "Nowe konto"}
      podtytul={reset ? `dla konta ${opis.targetNick ?? ""}` : "Wybierz nick i hasło"}
    >
      <form onSubmit={wyslij} className="space-y-3">
        {reset ? null : (
          <Pole
            value={nick}
            onChange={(event) => setNick(event.target.value)}
            placeholder="Nick — tak zobaczą Cię inni"
            autoCapitalize="off"
            autoCorrect="off"
            enterKeyHint="next"
          />
        )}
        <Pole
          value={haslo}
          onChange={(event) => setHaslo(event.target.value)}
          placeholder="Hasło (min. 8 znaków)"
          type="password"
          autoComplete="new-password"
        />
        <Pole
          value={haslo2}
          onChange={(event) => setHaslo2(event.target.value)}
          placeholder="Powtórz hasło"
          type="password"
          autoComplete="new-password"
          enterKeyHint="go"
        />
        {blad ? <Komunikat rodzaj="blad">{blad}</Komunikat> : null}
        <Przycisk
          type="submit"
          wariant="glowny"
          pelna
          disabled={trwa || haslo.length < 8 || (!reset && nick.length < 2)}
        >
          {trwa ? "Chwileczkę…" : reset ? "Ustaw hasło" : "Załóż konto"}
        </Przycisk>
      </form>
    </Ekran>
  );
}
